import React, { useState, useEffect } from "react";
import axios from "axios";
import { saveAs } from "file-saver";
import API_BASE_URL from "../config";
import {
  vmPageShell,
  vmTableScrollWrap,
  vmRecordRowBg,
  getVmRecordTableStyle,
  getVmRecordThStyle,
  getVmRecordTdStyle,
  vmRecordColGroupReact,
} from "./vehicleManagementLayout";
import { ViewMediaEyeButton } from "../../Components/ViewMediaEye";
import { resolveUploadUrl } from "../../lib/resolveUploadUrl";

const SCHEDULE_DEFAULTS = {
  "Pickup Truck": { scheduleInterval: "2", scheduleLitres: "40",  scheduleKmPerLitre: "8"   },
  "407 Truck":    { scheduleInterval: "2", scheduleLitres: "60",  scheduleKmPerLitre: "6"   },
  "17FT":         { scheduleInterval: "3", scheduleLitres: "80",  scheduleKmPerLitre: "5"   },
  "20FT":         { scheduleInterval: "3", scheduleLitres: "100", scheduleKmPerLitre: "4.5" },
  "Truck":        { scheduleInterval: "3", scheduleLitres: "80",  scheduleKmPerLitre: "5"   },
  "Bus":          { scheduleInterval: "2", scheduleLitres: "70",  scheduleKmPerLitre: "5"   },
  "Car":          { scheduleInterval: "7", scheduleLitres: "40",  scheduleKmPerLitre: "12"  },
  "Bike":         { scheduleInterval: "5", scheduleLitres: "10",  scheduleKmPerLitre: "40"  },
};

const emptyForm = {
  vehicleNumber: "", registerName: "", vehicleType: "", grossVehicleWeight: "",
  chassisNumber: "", engineNumber: "", fuelType: "",
  registrationDate: "", fitnessValidUpto: "", taxValidUpto: "", insuranceValidUpto: "",
  pollutionValidUpto: "",
  statePermit: "No", statePermitValidUpto: "",
  nationalPermit: "No", permitUpto: "",
  temporaryPermit: "No", TemporarypermitUpto: "", remarks: "",
  scheduleDate: "", scheduleInterval: "", scheduleLitres: "",
  scheduleKmPerLitre: "", scheduleKmPerFill: "", scheduleKmActual: "",
};

const VEHICLE_ONBOARDING_RECORD_HEADERS = [
  "Vehicle No", "Owner", "Type", "Chassis", "Engine", "Fuel", "GVW", "Reg Date", "Fitness", "Tax", "Insurance",
  "Pollution", "State Permit", "Nat. Permit", "Permit Upto",
  "Sched Date", "Interval", "Litres/Fill", "KM/Litre", "KM/Fill", "Odometer KM",
  "RC", "Insurance Doc", "Pollution Doc", "Road Tax", "Fitness Doc", "Permit Doc",
  "Schedule date history",
  "Edit",
  "Edit",
  "Remarks",
];

const VehicleOnboardingForm = () => {
  const [formData, setFormData] = useState(emptyForm);
  const [documents, setDocuments] = useState({
    RegistrationCertificate: null, Insurance: null, PollutionCertificate: null,
    RoadTax: null, FitnessCertificate: null, Permit: null, StatePermit: null, TemporaryPermit: null,
  });
  const [vehicleData, setVehicleData] = useState([]);
  const [searchTerm, setSearchTerm]   = useState("");
  const [editSched,  setEditSched]    = useState(null);
  const [schedSaving,setSchedSaving]  = useState(false);
  const [schedSuccess,setSchedSuccess]= useState("");
  const [schedHistoryModal, setSchedHistoryModal] = useState(null);
  const [entryFormOpen, setEntryFormOpen] = useState(false);
  /** "create" | "edit" — edit saves via PATCH and does not replace files unless new files are chosen. */
  const [formMode, setFormMode] = useState("create");
  const [isMobile, setIsMobile]       = useState(window.innerWidth <= 768);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  const autoKmPerFill = formData.scheduleLitres && formData.scheduleKmPerLitre
    ? Math.round(parseFloat(formData.scheduleLitres) * parseFloat(formData.scheduleKmPerLitre)) : "";

  const autoEditKmPerFill = editSched?.scheduleLitres && editSched?.scheduleKmPerLitre
    ? Math.round(parseFloat(editSched.scheduleLitres) * parseFloat(editSched.scheduleKmPerLitre)) : "";

  // ── Consistent style constants ──────────────────────────────────────────────
  const C = {
    yellow: "#FFC107",
    black:  "#000000",
    white:  "#ffffff",
    border: "1.5px solid #000000",
    borderLight: "1px solid #f0f0f0",
    borderYellow: "2px solid #FFC107",
    radius: "4px",
    fontFamily: "Arial, sans-serif",
    fontSize: "14px",
    fontSizeSm: "13px",
  };

  const S = {
    container: {
      ...vmPageShell(isMobile),
      fontFamily: C.fontFamily,
      backgroundColor: C.white, color: C.black,
      minHeight: "calc(100vh - 70px)", boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
    },
    pageHeader: {
      backgroundColor: C.yellow, color: C.black, padding: "16px 20px",
      fontWeight: "bold", fontSize: "20px", letterSpacing: "1px", textTransform: "uppercase",
    },
    formWrapper: { padding: isMobile ? "14px" : "20px" },
    sectionTitle: {
      fontWeight: "bold", fontSize: C.fontSizeSm, color: C.black,
      borderBottom: C.borderYellow, paddingBottom: "6px",
      marginBottom: "16px", marginTop: "20px",
      textTransform: "uppercase", letterSpacing: "0.5px",
    },
    formGrid: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(240px, 1fr))",
      gap: "16px", marginBottom: "16px",
    },
    label: { fontWeight: "bold", fontSize: C.fontSizeSm, marginBottom: "5px", display: "block", color: C.black },
    input: {
      width: "100%", padding: "9px 10px", border: C.border, borderRadius: C.radius,
      fontSize: isMobile ? "16px" : C.fontSize, boxSizing: "border-box",
      color: C.black, backgroundColor: C.white, outline: "none", fontFamily: C.fontFamily,
    },
    readOnly: {
      width: "100%", padding: "9px 10px", border: "1.5px solid #ccc", borderRadius: C.radius,
      fontSize: isMobile ? "16px" : C.fontSize, boxSizing: "border-box",
      color: "#555", backgroundColor: "#f9f9f9", outline: "none", fontFamily: C.fontFamily,
    },
    noteText: { fontSize: "12px", color: "#888", marginTop: "3px", display: "block" },
    buttonRow: { display: "flex", gap: "12px", justifyContent: "flex-end", marginTop: "10px", paddingBottom: "10px", flexWrap: "wrap" },
    btnBlack: { padding: "9px 28px", border: "none", borderRadius: C.radius, cursor: "pointer", backgroundColor: C.black, color: C.white, fontWeight: "bold", fontSize: C.fontSize, fontFamily: C.fontFamily },
    btnYellow: { padding: "9px 28px", border: "none", borderRadius: C.radius, cursor: "pointer", backgroundColor: C.yellow, color: C.black, fontWeight: "bold", fontSize: C.fontSize, fontFamily: C.fontFamily },
    searchContainer: { padding: "14px 20px", display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" },
    searchInput: { width: "320px", maxWidth: "100%", padding: "9px 12px", border: C.border, borderRadius: C.radius, fontSize: C.fontSize, color: C.black, fontFamily: C.fontFamily },
    table: getVmRecordTableStyle(isMobile, VEHICLE_ONBOARDING_RECORD_HEADERS.length),
    th: getVmRecordThStyle(isMobile),
    td: { ...getVmRecordTdStyle(isMobile), color: C.black },
    downloadBtn: { padding: "9px 22px", backgroundColor: C.yellow, color: C.black, border: "none", borderRadius: C.radius, cursor: "pointer", fontWeight: "bold", fontSize: C.fontSize, margin: "14px 20px", fontFamily: C.fontFamily },
    fileLabel: { fontWeight: "bold", fontSize: C.fontSizeSm, marginBottom: "5px", display: "block", color: C.black },
    fileSuccess: { fontSize: "12px", color: "green", marginTop: "4px" },
    autoTag: { fontSize: "11px", backgroundColor: "#f5f5f5", color: C.black, border: "1px solid #ccc", borderRadius: "3px", padding: "1px 6px", marginLeft: "6px", fontWeight: "bold" },
    // Modal
    overlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" },
    modal: { background: C.white, borderRadius: "6px", width: "100%", maxWidth: isMobile ? "98vw" : "540px", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 4px 24px rgba(0,0,0,0.2)" },
    modalHeader: { backgroundColor: C.yellow, color: C.black, padding: "14px 20px", fontWeight: "bold", fontSize: C.fontSize, borderRadius: "6px 6px 0 0", display: "flex", justifyContent: "space-between", alignItems: "center" },
    modalBody: { padding: isMobile ? "14px" : "20px" },
    modalGrid: { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "14px", marginBottom: "14px" },
    modalGroup: { display: "flex", flexDirection: "column", gap: "4px" },
    successMsg: { backgroundColor: "#f5f5f5", color: C.black, padding: "10px 14px", borderRadius: C.radius, fontSize: C.fontSizeSm, marginBottom: "12px", border: "1px solid #ccc", fontWeight: "bold" },
  };

  useEffect(() => {
    axios.get(API_BASE_URL + "/onboarding/all-vehicles")
      .then((res) => setVehicleData(res.data))
      .catch((err) => console.error(err));
  }, []);

  const dateIn = (d) => (d ? new Date(d).toISOString().split("T")[0] : "");

  const vehicleRowToFormData = (v) => ({
    vehicleNumber: v.vehicleNumber || "",
    registerName: v.registerName || "",
    vehicleType: v.vehicleType || "",
    grossVehicleWeight: v.grossVehicleWeight != null ? String(v.grossVehicleWeight) : "",
    chassisNumber: v.chassisNumber || "",
    engineNumber: v.engineNumber || "",
    fuelType: v.fuelType || "",
    registrationDate: dateIn(v.registrationDate),
    fitnessValidUpto: dateIn(v.fitnessValidUpto),
    taxValidUpto: dateIn(v.taxValidUpto),
    insuranceValidUpto: dateIn(v.insuranceValidUpto),
    pollutionValidUpto: dateIn(v.pollutionValidUpto),
    statePermit: v.statePermit || "No",
    statePermitValidUpto: dateIn(v.statePermitValidUpto),
    nationalPermit: v.nationalPermit || "No",
    permitUpto: dateIn(v.permitUpto),
    temporaryPermit: v.temporaryPermit || "No",
    TemporarypermitUpto: dateIn(v.TemporarypermitUpto),
    remarks: v.remarks || "",
    scheduleDate: dateIn(v.scheduleDate),
    scheduleInterval: v.scheduleInterval != null && v.scheduleInterval !== "" ? String(v.scheduleInterval) : "",
    scheduleLitres: v.scheduleLitres != null && v.scheduleLitres !== "" ? String(v.scheduleLitres) : "",
    scheduleKmPerLitre: v.scheduleKmPerLitre != null && v.scheduleKmPerLitre !== "" ? String(v.scheduleKmPerLitre) : "",
    scheduleKmPerFill: v.scheduleKmPerFill != null && v.scheduleKmPerFill !== "" ? String(v.scheduleKmPerFill) : "",
    scheduleKmActual: v.scheduleKmActual != null && v.scheduleKmActual !== "" ? String(v.scheduleKmActual) : "",
  });

  const openEditVehicle = (v) => {
    setFormMode("edit");
    setFormData(vehicleRowToFormData(v));
    setDocuments({
      RegistrationCertificate: null, Insurance: null, PollutionCertificate: null,
      RoadTax: null, FitnessCertificate: null, Permit: null, StatePermit: null, TemporaryPermit: null,
    });
    setEntryFormOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = {
        ...prev, [name]: value,
        ...(name === "nationalPermit"  && value === "No" ? { permitUpto: "" }           : {}),
        ...(name === "statePermit"     && value === "No" ? { statePermitValidUpto: "" } : {}),
        ...(name === "temporaryPermit" && value === "No" ? { TemporarypermitUpto: "" } : {}),
      };
      if (name === "vehicleType" && value && SCHEDULE_DEFAULTS[value]) {
        const def = SCHEDULE_DEFAULTS[value];
        if (!prev.scheduleInterval)   updated.scheduleInterval   = def.scheduleInterval   || "";
        if (!prev.scheduleLitres)     updated.scheduleLitres     = def.scheduleLitres     || "";
        if (!prev.scheduleKmPerLitre) updated.scheduleKmPerLitre = def.scheduleKmPerLitre || "";
      }
      return updated;
    });
  };

  const handleFileUpload = (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!["application/pdf","image/jpeg","image/png"].includes(file.type)) { alert("Only PDF, JPG, PNG allowed."); return; }
    setDocuments((prev) => ({ ...prev, [docType]: file }));
  };

  const validateForm = () => {
    const an = /^[a-zA-Z0-9 ]+$/;
    const al = /^[a-zA-Z ]+$/;
    if (!an.test(formData.vehicleNumber))     { alert("Vehicle Number: alphanumeric only."); return false; }
    if (!al.test(formData.registerName))       { alert("Register Name: alphabets only."); return false; }
    if (!formData.vehicleType?.trim())         { alert("Vehicle Type: please select a type (required for Home dashboard)."); return false; }
    if (!an.test(formData.grossVehicleWeight)) { alert("GVW: alphanumeric only."); return false; }
    return true;
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    const submitData = { ...formData, scheduleKmPerFill: formData.scheduleKmPerFill || autoKmPerFill };
    try {
      if (formMode === "edit") {
        await axios.patch(
          `${API_BASE_URL}/onboarding/vehicle/${encodeURIComponent(formData.vehicleNumber)}`,
          submitData
        );
        alert("Vehicle updated successfully!");
      } else {
        const fd = new FormData();
        Object.entries(submitData).forEach(([k, v]) => fd.append(k, v));
        Object.entries(documents).forEach(([k, f]) => { if (f) fd.append(k, f); });
        await axios.post(API_BASE_URL + "/onboarding/vehicle", fd);
        alert("Vehicle Onboarded Successfully!");
      }
      setFormData(emptyForm);
      setFormMode("create");
      setEntryFormOpen(false);
      setDocuments({ RegistrationCertificate: null, Insurance: null, PollutionCertificate: null, RoadTax: null, FitnessCertificate: null, Permit: null, StatePermit: null, TemporaryPermit: null });
      const res = await axios.get(API_BASE_URL + "/onboarding/all-vehicles");
      setVehicleData(res.data);
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.message || err.message || "Submission failed. Please try again.";
      alert("Error: " + msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClear = () => {
    setFormData(emptyForm);
    setFormMode("create");
    setDocuments({
      RegistrationCertificate: null, Insurance: null, PollutionCertificate: null,
      RoadTax: null, FitnessCertificate: null, Permit: null, StatePermit: null, TemporaryPermit: null,
    });
  };

  const downloadCSV = () => {
    axios.get(API_BASE_URL + "/onboarding/vehicle-csv", { responseType: "blob" })
      .then((res) => saveAs(res.data, "VehicleOnboardingData.csv"))
      .catch((err) => console.error(err));
  };

  // ── Schedule edit modal ─────────────────────────────────────────────────────
  const openEditSched = (v) => {
    setEditSched({
      vehicleNumber:      v.vehicleNumber,
      scheduleDate:       v.scheduleDate ? new Date(v.scheduleDate).toISOString().split("T")[0] : "",
      scheduleInterval:   v.scheduleInterval   || "",
      scheduleLitres:     v.scheduleLitres     || "",
      scheduleKmPerLitre: v.scheduleKmPerLitre || "",
      scheduleKmPerFill:  v.scheduleKmPerFill  || "",
      scheduleKmActual:   v.scheduleKmActual   || "",
    });
    setSchedSuccess("");
  };

  const handleEditSchedChange = (e) => {
    const { name, value } = e.target;
    setEditSched((prev) => ({ ...prev, [name]: value }));
  };

  const openScheduleDateHistory = (v) => {
    const raw = Array.isArray(v.scheduleDateHistory) ? v.scheduleDateHistory : [];
    const sorted = [...raw].sort(
      (a, b) => new Date(b.changedAt || 0) - new Date(a.changedAt || 0)
    );
    setSchedHistoryModal({
      vehicleNumber: v.vehicleNumber,
      currentScheduleDate: v.scheduleDate || null,
      items: sorted,
    });
  };

  const sameScheduleCalendarDay = (a, b) => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    const da = new Date(a);
    const db = new Date(b);
    if (isNaN(da.getTime()) || isNaN(db.getTime())) return false;
    return da.toISOString().split("T")[0] === db.toISOString().split("T")[0];
  };

  const fmtHistDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN") : "—");
  const fmtHistWhen = (d) => (d ? new Date(d).toLocaleString("en-IN") : "—");
  const fmtHistSource = (s) => (s === "onboarding" ? "Onboarding" : s === "edit" ? "Edit" : s || "—");

  const saveEditSched = async () => {
    if (!editSched?.scheduleDate) {
      alert("Next schedule date is required.");
      return;
    }
    const iv = Number(editSched.scheduleInterval);
    if (!editSched.scheduleInterval || iv < 1 || iv > 31) {
      alert("Refill interval must be between 1 and 31 days.");
      return;
    }
    const lit = Number(editSched.scheduleLitres);
    if (!editSched.scheduleLitres || lit <= 0) {
      alert("Litres per fill must be greater than 0.");
      return;
    }
    const kpl = Number(editSched.scheduleKmPerLitre);
    if (editSched.scheduleKmPerLitre === "" || editSched.scheduleKmPerLitre == null || Number.isNaN(kpl) || kpl <= 0) {
      alert("KM per litre must be greater than 0.");
      return;
    }
    setSchedSaving(true);
    try {
      await axios.patch(
        `${API_BASE_URL}/onboarding/vehicle/${editSched.vehicleNumber}/schedule`,
        {
          scheduleDate:       editSched.scheduleDate       || null,
          scheduleInterval:   editSched.scheduleInterval   ? Number(editSched.scheduleInterval)   : null,
          scheduleLitres:     editSched.scheduleLitres     ? Number(editSched.scheduleLitres)     : null,
          scheduleKmPerLitre: editSched.scheduleKmPerLitre ? Number(editSched.scheduleKmPerLitre) : null,
          scheduleKmPerFill:  editSched.scheduleKmPerFill  ? Number(editSched.scheduleKmPerFill)
                              : autoEditKmPerFill           ? autoEditKmPerFill                    : null,
          scheduleKmActual:   editSched.scheduleKmActual   ? Number(editSched.scheduleKmActual)   : null,
        }
      );
      setSchedSuccess("Schedule updated successfully!");
      const res = await axios.get(API_BASE_URL + "/onboarding/all-vehicles");
      setVehicleData(res.data);
      setTimeout(() => { setEditSched(null); setSchedSuccess(""); }, 1200);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to save schedule.";
      alert("Failed to save schedule: " + msg);
      console.error(err);
    }
    finally { setSchedSaving(false); }
  };

  const filteredVehicles = vehicleData.filter((v) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    const hay = [v.vehicleNumber, v.registerName, v.vehicleType, v.chassisNumber, v.engineNumber, v.fuelType]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });

  return (
    <div style={S.container}>
      <div style={S.pageHeader}>VEHICLE ONBOARDING</div>

      <div style={S.formWrapper}>
        <div style={S.sectionTitle}>{formMode === "edit" ? "Edit" : "Add vehicle"}</div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: entryFormOpen ? "14px" : "20px",
            flexWrap: "wrap",
            width: "100%",
          }}
        >
          <button
            type="button"
            style={{ ...S.btnYellow, ...(entryFormOpen ? { opacity: 0.85 } : {}) }}
            onClick={() => {
              setFormMode("create");
              setFormData(emptyForm);
              setDocuments({
                RegistrationCertificate: null, Insurance: null, PollutionCertificate: null,
                RoadTax: null, FitnessCertificate: null, Permit: null, StatePermit: null, TemporaryPermit: null,
              });
              setEntryFormOpen(true);
            }}
            disabled={entryFormOpen}
          >
            Add Vehicle
          </button>
          {entryFormOpen ? (
            <button
              type="button"
              aria-label="Close form"
              title="Close form"
              disabled={submitting}
              onClick={() => {
                setEntryFormOpen(false);
                handleClear();
              }}
              style={{
                marginLeft: "auto",
                background: "none",
                border: "none",
                fontSize: "18px",
                lineHeight: 1,
                color: C.black,
                fontFamily: C.fontFamily,
                fontWeight: "bold",
                padding: "4px 8px",
                minHeight: 44,
                minWidth: 44,
                cursor: submitting ? "not-allowed" : "pointer",
                touchAction: "manipulation",
                opacity: submitting ? 0.5 : 1,
                flexShrink: 0,
              }}
            >
              ✕
            </button>
          ) : null}
        </div>
        {entryFormOpen ? (
        <form onSubmit={handleSubmit}>

          {/* Vehicle Information */}
          <div style={S.sectionTitle}>Vehicle Information</div>
          <div style={S.formGrid}>
            <div>
              <label style={S.label}>Vehicle Number:</label>
              <input type="text" style={{ ...S.input, ...(formMode === "edit" ? { backgroundColor: "#f0f0f0", color: "#555" } : {}) }} name="vehicleNumber"
                value={formData.vehicleNumber} onChange={handleChange} required readOnly={formMode === "edit"} />
            </div>
            <div>
              <label style={S.label}>Register Owner Name:</label>
              <input type="text" style={S.input} name="registerName"
                value={formData.registerName} onChange={handleChange} required />
            </div>
            <div>
              <label style={S.label}>Vehicle Type:</label>
              <select name="vehicleType" style={S.input} value={formData.vehicleType} onChange={handleChange} required>
                <option value="">Select Type</option>
                <optgroup label="Fleet Trucks">
                  <option value="Pickup Truck">Pickup Truck</option>
                  <option value="407 Truck">407 Truck</option>
                  <option value="17FT">17FT</option>
                  <option value="20FT">20FT</option>
                  <option value="Truck">Truck (Other)</option>
                </optgroup>
                <optgroup label="Other Vehicles">
                  <option value="Bus">Bus</option>
                  <option value="Car">Car</option>
                  <option value="Bike">Bike</option>
                </optgroup>
              </select>
            </div>
            <div>
              <label style={S.label}>Gross Vehicle Weight:</label>
              <input type="text" style={S.input} name="grossVehicleWeight"
                value={formData.grossVehicleWeight} onChange={handleChange} required />
            </div>
            <div>
              <label style={S.label}>Registration Date:</label>
              <input type="date" style={S.input} name="registrationDate"
                value={formData.registrationDate} onChange={handleChange} required />
            </div>
          </div>

          <div style={S.sectionTitle}>Identification</div>
          <div style={S.formGrid}>
            <div>
              <label style={S.label}>Chassis Number:</label>
              <input type="text" style={S.input} name="chassisNumber" value={formData.chassisNumber} onChange={handleChange} />
            </div>
            <div>
              <label style={S.label}>Engine Number:</label>
              <input type="text" style={S.input} name="engineNumber" value={formData.engineNumber} onChange={handleChange} />
            </div>
            <div>
              <label style={S.label}>Fuel Type:</label>
              <select name="fuelType" style={S.input} value={formData.fuelType} onChange={handleChange}>
                <option value="">—</option>
                <option value="Diesel">Diesel</option>
                <option value="Petrol">Petrol</option>
                <option value="CNG">CNG</option>
                <option value="Electric">Electric</option>
                <option value="Hybrid">Hybrid</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          {/* Document Validity */}
          <div style={S.sectionTitle}>Document Validity Dates</div>
          <div style={S.formGrid}>
            <div>
              <label style={S.label}>Fitness Valid Upto:</label>
              <input type="date" style={S.input} name="fitnessValidUpto"
                value={formData.fitnessValidUpto} onChange={handleChange} required />
            </div>
            <div>
              <label style={S.label}>Tax Valid Upto:</label>
              <input type="date" style={S.input} name="taxValidUpto"
                value={formData.taxValidUpto} onChange={handleChange} required />
            </div>
            <div>
              <label style={S.label}>Insurance Valid Upto:</label>
              <input type="date" style={S.input} name="insuranceValidUpto"
                value={formData.insuranceValidUpto} onChange={handleChange} required />
            </div>
            <div>
              <label style={S.label}>Pollution Valid Upto:</label>
              <input type="date" style={S.input} name="pollutionValidUpto"
                value={formData.pollutionValidUpto} onChange={handleChange} required />
            </div>

            {/* State Permit — Yes/No then date */}
            <div>
              <label style={S.label}>State Permit:</label>
              <select name="statePermit" style={S.input} value={formData.statePermit} onChange={handleChange} required>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </div>
            {formData.statePermit === "Yes" && (
              <div>
                <label style={S.label}>State Permit Valid Upto:</label>
                <input type="date" style={S.input} name="statePermitValidUpto"
                  value={formData.statePermitValidUpto} onChange={handleChange} required />
              </div>
            )}

            {/* National Permit — Yes/No then date */}
            <div>
              <label style={S.label}>National Permit:</label>
              <select name="nationalPermit" style={S.input} value={formData.nationalPermit} onChange={handleChange} required>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </div>
            {formData.nationalPermit === "Yes" && (
              <div>
                <label style={S.label}>National Permit Upto:</label>
                <input type="date" style={S.input} name="permitUpto"
                  value={formData.permitUpto} onChange={handleChange} required />
              </div>
            )}

            {/* Temporary Permit — Yes/No then date */}
            <div>
              <label style={S.label}>Temporary Permit:</label>
              <select name="temporaryPermit" style={S.input} value={formData.temporaryPermit} onChange={handleChange} required>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </div>
            {formData.temporaryPermit === "Yes" && (
              <div>
                <label style={S.label}>Temporary Permit Upto:</label>
                <input type="date" style={S.input} name="TemporarypermitUpto"
                  value={formData.TemporarypermitUpto} onChange={handleChange} required />
              </div>
            )}
          </div>

          {/* Schedule Configuration */}
          <div style={S.sectionTitle}>Schedule Configuration</div>
          <div style={S.formGrid}>
            <div>
              <label style={S.label}>Schedule Date:</label>
              <input type="date" style={S.input} name="scheduleDate"
                value={formData.scheduleDate} onChange={handleChange} />
            </div>
            <div>
              <label style={S.label}>Refill interval (days):</label>
              <input type="number" style={S.input} name="scheduleInterval"
                min="1" max="31"
                value={formData.scheduleInterval} onChange={handleChange} />
            </div>
            <div>
              <label style={S.label}>Litres per Fill:</label>
              <input type="number" style={S.input} name="scheduleLitres"
                min="1" step="0.1"
                value={formData.scheduleLitres} onChange={handleChange} />
            </div>
            <div>
              <label style={S.label}>KM per Litre (Mileage):</label>
              <input type="number" style={S.input} name="scheduleKmPerLitre"
                min="0" step="0.1"
                value={formData.scheduleKmPerLitre} onChange={handleChange} />
            </div>
            <div>
              <label style={S.label}>
                Expected KM per Fill:
                {autoKmPerFill && !formData.scheduleKmPerFill && (
                  <span style={S.autoTag}>AUTO</span>
                )}
              </label>
              <input type="number" style={S.input} name="scheduleKmPerFill"
                placeholder={autoKmPerFill ? `${autoKmPerFill}` : ""}
                value={formData.scheduleKmPerFill || autoKmPerFill}
                onChange={handleChange} />
            </div>
          </div>

          {/* Remarks */}
          <div style={{ marginTop: "16px", marginBottom: "16px" }}>
            <label style={S.label}>Remarks:</label>
            <textarea name="remarks"
              style={{ ...S.input, minHeight: "70px", resize: "vertical" }}
              value={formData.remarks} onChange={handleChange} />
          </div>

          {/* Document Uploads */}
          <div style={S.sectionTitle}>Document Uploads</div>
          {formMode === "edit" && (
            <p style={{ fontSize: "12px", color: "#666", marginBottom: "16px", marginTop: "-6px", maxWidth: "720px", lineHeight: 1.45 }}>
              PDF or image replacements are done from Vehicle Documents (renew). Saving here updates registration, permits, schedule fields, and remarks only.
            </p>
          )}
          <div style={S.formGrid}>
            {/* Always shown */}
            {["RegistrationCertificate","Insurance","PollutionCertificate","RoadTax","FitnessCertificate"].map((docType) => (
              <div key={docType}>
                <label style={S.fileLabel}>{docType.replace(/([A-Z])/g, " $1").trim()}:</label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileUpload(e, docType)}
                  style={{ fontSize: "13px" }} />
                {documents[docType] && <p style={S.fileSuccess}>Uploaded: {documents[docType].name}</p>}
              </div>
            ))}
            {/* State Permit document — only if State Permit = Yes */}
            {formData.statePermit === "Yes" && (
              <div>
                <label style={S.fileLabel}>State Permit Document:</label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileUpload(e, "StatePermit")}
                  style={{ fontSize: "13px" }} />
                {documents["StatePermit"] && <p style={S.fileSuccess}>Uploaded: {documents["StatePermit"].name}</p>}
              </div>
            )}
            {/* National Permit document — only if National Permit = Yes */}
            {formData.nationalPermit === "Yes" && (
              <div>
                <label style={S.fileLabel}>National Permit Document:</label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileUpload(e, "Permit")}
                  style={{ fontSize: "13px" }} />
                {documents["Permit"] && <p style={S.fileSuccess}>Uploaded: {documents["Permit"].name}</p>}
              </div>
            )}
            {/* Temporary Permit document — only if Temporary Permit = Yes */}
            {formData.temporaryPermit === "Yes" && (
              <div>
                <label style={S.fileLabel}>Temporary Permit Document:</label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileUpload(e, "TemporaryPermit")}
                  style={{ fontSize: "13px" }} />
                {documents["TemporaryPermit"] && <p style={S.fileSuccess}>Uploaded: {documents["TemporaryPermit"].name}</p>}
              </div>
            )}
          </div>

          <div style={S.buttonRow}>
            <button type="button" style={S.btnBlack} onClick={() => { setEntryFormOpen(false); handleClear(); }} disabled={submitting}>Cancel</button>
            <button type="button" style={S.btnBlack} onClick={handleClear} disabled={submitting}>Clear</button>
            <button type="submit" style={{ ...S.btnYellow, opacity: submitting ? 0.7 : 1 }} disabled={submitting}>
              {submitting ? "Saving…" : formMode === "edit" ? "Save changes" : "Submit"}
            </button>
          </div>
        </form>
        ) : null}
      </div>

      {/* Records Table */}
      <div style={S.searchContainer}>
        <input type="text" placeholder="Search…"
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          style={S.searchInput} />
      </div>

      <div style={vmTableScrollWrap}>
        <table style={S.table}>
          {isMobile ? vmRecordColGroupReact(VEHICLE_ONBOARDING_RECORD_HEADERS.length) : null}
          <thead>
            <tr>
              {VEHICLE_ONBOARDING_RECORD_HEADERS.map((h, hi) => <th key={hi} style={S.th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filteredVehicles.length > 0 ? (
              filteredVehicles.map((v, i) => (
                <tr key={i} style={{ backgroundColor: vmRecordRowBg(i) }}>
                  <td style={S.td}>{v.vehicleNumber}</td>
                  <td style={S.td}>{v.registerName}</td>
                  <td style={S.td}>{v.vehicleType}</td>
                  <td style={S.td}>{v.chassisNumber || "—"}</td>
                  <td style={S.td}>{v.engineNumber || "—"}</td>
                  <td style={S.td}>{v.fuelType || "—"}</td>
                  <td style={S.td}>{v.grossVehicleWeight}</td>
                  <td style={S.td}>{v.registrationDate     ? new Date(v.registrationDate).toLocaleDateString("en-IN")     : ""}</td>
                  <td style={S.td}>{v.fitnessValidUpto     ? new Date(v.fitnessValidUpto).toLocaleDateString("en-IN")     : ""}</td>
                  <td style={S.td}>{v.taxValidUpto         ? new Date(v.taxValidUpto).toLocaleDateString("en-IN")         : ""}</td>
                  <td style={S.td}>{v.insuranceValidUpto   ? new Date(v.insuranceValidUpto).toLocaleDateString("en-IN")   : ""}</td>
                  <td style={S.td}>{v.pollutionValidUpto   ? new Date(v.pollutionValidUpto).toLocaleDateString("en-IN")   : ""}</td>
                  <td style={S.td}>{v.statePermitValidUpto ? new Date(v.statePermitValidUpto).toLocaleDateString("en-IN") : ""}</td>
                  <td style={S.td}>{v.nationalPermit}</td>
                  <td style={S.td}>{v.permitUpto           ? new Date(v.permitUpto).toLocaleDateString("en-IN")           : ""}</td>
                  <td style={S.td}>{v.scheduleDate         ? new Date(v.scheduleDate).toLocaleDateString("en-IN")         : ""}</td>
                  <td style={S.td}>{v.scheduleInterval     ? `${v.scheduleInterval} days` : ""}</td>
                  <td style={S.td}>{v.scheduleLitres       ? `${v.scheduleLitres} L`      : ""}</td>
                  <td style={S.td}>{v.scheduleKmPerLitre   ? `${v.scheduleKmPerLitre} km/L` : ""}</td>
                  <td style={S.td}>{v.scheduleKmPerFill    ? `${v.scheduleKmPerFill} km`  : ""}</td>
                  <td style={S.td}>{v.scheduleKmActual     ? `${v.scheduleKmActual} km`   : ""}</td>
                  {["RegistrationCertificate","Insurance","PollutionCertificate","RoadTax","FitnessCertificate","Permit"].map((fk) => (
                    <td key={fk} style={S.td}>
                      {v[fk] ? (
                        <ViewMediaEyeButton
                          onClick={() => {
                            const url = resolveUploadUrl(API_BASE_URL, v.filePaths?.[fk] || v[fk]);
                            if (url) window.open(url, "_blank", "noopener,noreferrer");
                          }}
                          ariaLabel="View document"
                          title="View document"
                        />
                      ) : "N/A"}
                    </td>
                  ))}
                  <td style={S.td}>
                    <button
                      type="button"
                      onClick={() => openScheduleDateHistory(v)}
                      style={{
                        padding: "5px 12px", backgroundColor: C.white, color: C.black, border: C.border,
                        borderRadius: C.radius, cursor: "pointer", fontWeight: "bold", fontSize: "12px", fontFamily: C.fontFamily,
                      }}
                    >
                      History
                    </button>
                  </td>
                  <td style={S.td}>
                    <button type="button" onClick={() => openEditSched(v)}
                      title="Edit schedule"
                      aria-label="Edit schedule"
                      style={{ padding: "5px 12px", backgroundColor: C.yellow, color: C.black, border: "none", borderRadius: C.radius, cursor: "pointer", fontWeight: "bold", fontSize: "12px", fontFamily: C.fontFamily }}>
                      Edit
                    </button>
                  </td>
                  <td style={S.td}>
                    <button type="button" onClick={() => openEditVehicle(v)}
                      title="Edit vehicle"
                      aria-label="Edit vehicle"
                      style={{ padding: "5px 12px", backgroundColor: C.white, color: C.black, border: C.border, borderRadius: C.radius, cursor: "pointer", fontWeight: "bold", fontSize: "12px", fontFamily: C.fontFamily }}>
                      Edit
                    </button>
                  </td>
                  <td style={S.td}>{v.remarks}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={VEHICLE_ONBOARDING_RECORD_HEADERS.length} style={{ ...S.td, textAlign:"center", padding:"24px", color:"#888" }}>
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <button style={S.downloadBtn} onClick={downloadCSV}>Download CSV</button>

      {/* Edit schedule (modal) */}
      {editSched && (
        <div style={S.overlay} onClick={(e) => { if (e.target === e.currentTarget) setEditSched(null); }}>
          <div style={S.modal}>
            <div style={S.modalHeader}>
              <span>Edit — {editSched.vehicleNumber}</span>
              <button onClick={() => setEditSched(null)}
                style={{ background:"none", border:"none", fontSize:"20px", cursor:"pointer", color: C.black }}>
                x
              </button>
            </div>
            <div style={S.modalBody}>
              {schedSuccess && <div style={S.successMsg}>{schedSuccess}</div>}

              <div style={S.modalGrid}>
                <div style={S.modalGroup}>
                  <label style={S.label}>Fill Interval (Days):</label>
                  <input type="number" style={S.input} name="scheduleInterval"
                    min="1" max="31"
                    value={editSched.scheduleInterval} onChange={handleEditSchedChange} />
                </div>
                <div style={S.modalGroup}>
                  <label style={S.label}>Next Schedule Date:</label>
                  <input type="date" style={S.input} name="scheduleDate"
                    value={editSched.scheduleDate} onChange={handleEditSchedChange} />
                </div>
                <div style={S.modalGroup}>
                  <label style={S.label}>Litres per Fill:</label>
                  <input type="number" style={S.input} name="scheduleLitres"
                    min="1" step="0.1"
                    value={editSched.scheduleLitres} onChange={handleEditSchedChange} />
                </div>
                <div style={S.modalGroup}>
                  <label style={S.label}>KM per Litre:</label>
                  <input type="number" style={S.input} name="scheduleKmPerLitre"
                    min="0" step="0.1"
                    value={editSched.scheduleKmPerLitre} onChange={handleEditSchedChange} />
                </div>
                <div style={S.modalGroup}>
                  <label style={S.label}>
                    Expected KM per Fill:
                    {autoEditKmPerFill && !editSched.scheduleKmPerFill && <span style={S.autoTag}>AUTO</span>}
                  </label>
                  <input type="number" style={S.input} name="scheduleKmPerFill"
                    placeholder={autoEditKmPerFill ? `${autoEditKmPerFill}` : ""}
                    value={editSched.scheduleKmPerFill || autoEditKmPerFill}
                    onChange={handleEditSchedChange} />
                </div>
                <div style={S.modalGroup}>
                  <label style={S.label}>Odometer KM (optional):</label>
                  <input type="number" style={S.input} name="scheduleKmActual"
                    value={editSched.scheduleKmActual} onChange={handleEditSchedChange} />
                </div>
              </div>

              <div style={{ display:"flex", justifyContent:"flex-end", gap:"12px", flexWrap:"wrap" }}>
                <button style={S.btnBlack} onClick={() => setEditSched(null)}>Cancel</button>
                <button style={S.btnYellow} onClick={saveEditSched} disabled={schedSaving}>
                  {schedSaving ? "Saving..." : "Save Schedule"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {schedHistoryModal && (
        <div
          style={S.overlay}
          onClick={(e) => { if (e.target === e.currentTarget) setSchedHistoryModal(null); }}
        >
          <div style={{ ...S.modal, maxWidth: isMobile ? "98vw" : "720px" }}>
            <div style={S.modalHeader}>
              <span>Schedule history · {schedHistoryModal.vehicleNumber}</span>
              <button
                type="button"
                onClick={() => setSchedHistoryModal(null)}
                style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: C.black }}
              >
                x
              </button>
            </div>
            <div style={S.modalBody}>
              <div style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "12px", color: C.black }}>
                Current schedule date: {fmtHistDate(schedHistoryModal.currentScheduleDate)}
              </div>
              {schedHistoryModal.items.length === 0 ? (
                <p style={{ textAlign: "center", color: "#888", padding: "20px" }}>No history yet.</p>
              ) : (
                <div style={vmTableScrollWrap}>
                  <table style={{ ...getVmRecordTableStyle(isMobile, 4), marginTop: 0 }}>
                    {isMobile ? vmRecordColGroupReact(4) : null}
                    <thead>
                      <tr>
                        <th style={S.th}>#</th>
                        <th style={S.th}>Schedule date saved</th>
                        <th style={S.th}>Saved at</th>
                        <th style={S.th}>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedHistoryModal.items.map((h, idx) => {
                        const matchesCurrent =
                          idx === 0 &&
                          sameScheduleCalendarDay(h.scheduleDate, schedHistoryModal.currentScheduleDate);
                        return (
                          <tr key={h._id || idx} style={{ backgroundColor: vmRecordRowBg(idx) }}>
                            <td style={S.td}>{idx + 1}</td>
                            <td style={S.td}>
                              {fmtHistDate(h.scheduleDate)}
                              {matchesCurrent ? (
                                <span style={{ ...S.autoTag, marginLeft: "8px" }}>current</span>
                              ) : null}
                            </td>
                            <td style={S.td}>{fmtHistWhen(h.changedAt)}</td>
                            <td style={S.td}>{fmtHistSource(h.source)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleOnboardingForm;
